import { Box, Link, Stack, Text } from '@solvhealth/jigsaw';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MEMBERSHIP_NAME } from '~/components/Membership/constants';
import { useOnMembershipEntrypointClicked } from '~/components/Membership/utils';
import {
  SOLV_PLUS_ENTRYPOINT_ACCOUNT_HOME,
  SOLV_PLUS_ENTRYPOINT_ACCOUNT_SIDEBAR,
} from '~/core/analytics/events';
import { isActiveMembership } from '~/core/util/account';
import useShouldShowMembershipEntrypoints from '~/hooks/membership/useShouldShowMembershipEntrypoints';
import { shouldShowAppInstallPrompt } from '../../../components/AddToHomeScreen/util';
import { checkMembershipEntrypointFeatureFlag } from '../../../config';
import { ACCOUNT_HOME_PATH } from '../../../constants';
import { isIosSafariOnly } from '../../../core/util/device';
import useToggle from '../../../hooks/useToggle';
import useDeleteUserProfile from '../hooks/useDeleteUserProfile';
import useOnProfileSelect from '../hooks/useOnProfileSelect';
import {
  useDownloadApp,
  useGoToAccountSettings,
  useGoToContactUs,
  useGoToMembership,
  useProfileBadge,
} from './AccountDrawer/hooks';
import DrawerTile from './DrawerTile';
import ProfileList from './ProfileList';

type Props = {
  onClose: () => void;
  currentProfile: any;
  handleLogout: () => void;
  accountSummary: any;
  toggleAddFamilyModal: () => void;
  toggleAboutUsModal: () => void;
  toggleMemberContactUsModal: () => void;
};

type DrawerMenuListItem = {
  title: string;
  visible: boolean;
  children: any;
  actionsList: any[];
};

const DrawerMenuList = (props: Props) => {
  const {
    onClose,
    currentProfile,
    handleLogout,
    accountSummary,
    toggleAddFamilyModal,
    toggleAboutUsModal,
    toggleMemberContactUsModal,
  } = props;

  const [isInEditMode, toggleInEditMode] = useToggle();
  const goToAccountSettings = useGoToAccountSettings(onClose);
  const goToMembership = useGoToMembership(onClose, accountSummary, ACCOUNT_HOME_PATH);
  const badgeList = useProfileBadge();
  const handleDeleteProfile = useDeleteUserProfile();
  const downloadApp = useDownloadApp(onClose);
  const goToContactUs = useGoToContactUs();
  const onProfileSelect = useOnProfileSelect(onClose);

  const { t } = useTranslation(['common', 'membership']);

  const getProfileActionsList = () => (
    <>
      <ProfileList
        accountSummary={accountSummary}
        badgeList={badgeList}
        currentProfile={currentProfile}
        deleteUserProfile={handleDeleteProfile}
        isInEditMode={isInEditMode}
        onSelect={onProfileSelect}
      />

      {!isInEditMode && (
        <>
          <Stack alignItems="center" justifyContent="flex-start" px={3} py={2} row space={3}>
            <Link
              onClick={toggleAddFamilyModal}
              startIcon={<img src="/images/account/plus_icon.svg" />}
              subtle
            >
              Add profile
            </Link>
            <Link
              onClick={toggleInEditMode}
              startIcon={<img src="/images/account/dash_icon.svg" />}
              subtle
            >
              Remove profile
            </Link>
          </Stack>
        </>
      )}
    </>
  );

  const showSolvPlus = useShouldShowMembershipEntrypoints({
    existingMembershipUsersShouldSeeThis: true,
    usersWithClinicRelationshipShouldSeeThis: false,
    entrypointId: SOLV_PLUS_ENTRYPOINT_ACCOUNT_SIDEBAR,
  });

  const { onClick: goToSolvPlusEntrypoint, isMember } = useOnMembershipEntrypointClicked(
    SOLV_PLUS_ENTRYPOINT_ACCOUNT_SIDEBAR
  );

  const handleEntrypointClick = () => {
    onClose();
    goToSolvPlusEntrypoint();
  };

  const drawerMenuList: Array<DrawerMenuListItem> = [
    {
      title: isInEditMode ? 'Remove Profile' : 'Profiles',
      visible: true,
      children: getProfileActionsList(),
      actionsList: [],
    },
    {
      title: 'Account',
      visible: !isInEditMode,
      children: null,
      actionsList: [
        {
          actionText: (
            <Stack display="block" space={0.5}>
              <Box
                bg="teal"
                borderRadius="sm"
                display="inline"
                fontSize="xs"
                fontWeight="bold"
                mr={1}
                sx={{ textTransform: 'uppercase', py: '2px', px: 0.5 }}
                textColor="white"
              >
                New
              </Box>
              <Text display="inline" textColor="teal-dark">
                {t('membership:account.drawerCta', { MEMBERSHIP_NAME })}
              </Text>
            </Stack>
          ),
          onClick: handleEntrypointClick,
          visible: showSolvPlus && !isMember,
        },
        { actionText: 'Settings', onClick: goToAccountSettings, visible: true },
        {
          actionText: 'Membership and billing',
          onClick: goToMembership,
          visible: showSolvPlus && isMember,
        },
      ],
    },
    {
      title: 'More',
      visible: !isInEditMode,
      children: null,
      actionsList: [
        { actionText: 'About Us', onClick: toggleAboutUsModal, visible: true },
        {
          actionText: 'Download App',
          onClick: downloadApp,
          visible: shouldShowAppInstallPrompt() || isIosSafariOnly(),
        },
        {
          actionText: 'Contact Us',
          onClick: isActiveMembership(accountSummary) ? toggleMemberContactUsModal : goToContactUs,
          visible: true,
        },
        { actionText: 'Log Out', onClick: handleLogout, visible: true },
      ],
    },
    {
      title: '',
      visible: isInEditMode,
      children: null,
      actionsList: [
        {
          actionText: 'Done',
          onClick: toggleInEditMode,
          showArrow: false,
          isTextLink: true,
          visible: isInEditMode,
        },
      ],
    },
  ].filter((tile) => tile.visible);

  return (
    <>
      {drawerMenuList.map((item, i) => (
        <div key={item.title}>
          <DrawerTile actionsList={item.actionsList.filter((l) => l.visible)} title={item.title}>
            {item.children}
          </DrawerTile>
          {i !== drawerMenuList.length - 1 && !isInEditMode && (
            <Box border="solid 3px" borderColor="gray-100" />
          )}
        </div>
      ))}
    </>
  );
};

export default DrawerMenuList;
